// Markup from design/kit/components/Dialog: scrim, modal, p-head with dtitle, subline and xbtn,
// p-body, m-foot. Esc and the scrim close it; focus moves in on open and back on close, Tab stays
// inside, and the page behind does not scroll.
import { useEffect, useRef, type ReactNode } from 'react';
import { XIcon } from './icons';
import { useModalLayer } from './modal-layer';

type DialogProps = {
  id: string;
  title: string;
  subline?: string;
  onClose: () => void;
  footer: ReactNode;
  children: ReactNode;
};

export function Dialog({ id, title, subline, onClose, footer, children }: DialogProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  useModalLayer(boxRef);

  // Focus moves into the dialog when it opens and back to what opened it when it closes.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const first = boxRef.current?.querySelector<HTMLElement>(
      '.p-body input, .p-body textarea, .p-body button',
    );
    (first ?? boxRef.current)?.focus();
    return () => opener?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <button type="button" className="scrim" aria-label={`Close ${title}`} onClick={onClose} />
      <div
        ref={boxRef}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={id}
        tabIndex={-1}
      >
        <div className="p-head">
          <div className="col g4">
            <h2 id={id} className="dtitle">
              {title}
            </h2>
            {subline && <span className="t12 muted">{subline}</span>}
          </div>
          <button type="button" className="xbtn" aria-label="Close" onClick={onClose}>
            <XIcon />
          </button>
        </div>
        <div className="p-body">{children}</div>
        <div className="m-foot">{footer}</div>
      </div>
    </>
  );
}
